import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEnvelope,
  FaGoogle,
  FaCamera,
  FaDownload,
  FaTrash,
  FaExclamationTriangle,
  FaTimes,
  FaPen,
} from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store";
import { updateUser } from "@/store/slices/authSlice";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import api from "@/api/config";

interface ProfileFormData {
  name: string;
}

const MyAccountTab: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit } = useForm<ProfileFormData>({
    defaultValues: { name: user?.name || "" },
  });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    // open the edit form if not already open
    setIsEditingProfile(true);
  };

  const onSubmit = async (data: ProfileFormData) => {
    setIsUpdating(true);
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      if (photoFile) formData.append("profile_pic", photoFile);

      const response = await api.post("/api/profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(updateUser(response.data.user));
      setPhotoPreview(null);
      setPhotoFile(null);
      setIsEditingProfile(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      window.location.href = "/api/account/export";
      toast.success("Your data export is downloading");
    } catch {
      toast.error("Failed to export data");
    } finally {
      setTimeout(() => setIsExporting(false), 2000);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE MY ACCOUNT") return;
    setIsDeleting(true);
    try {
      await api.delete("/api/account/delete", {
        data: { confirm: "DELETE MY ACCOUNT" },
      });
      toast.success("Your account has been permanently deleted");
      setTimeout(() => (window.location.href = "/login"), 1500);
    } catch {
      toast.error(
        "Failed to delete account. Please try again or contact support.",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="my-account-tab">
      {/* Name and Photo */}
      <div className="tab-section">
        <div className="section-heading-row">
          <div>
            <h3 className="section-title">Name and Photo</h3>
            <p className="section-description">
              The name that appears for your profile.
            </p>
          </div>
        </div>

        <div className="profile-header-content">
          <div className="avatar-section">
            <div className="avatar-wrapper">
              {photoPreview || user?.profile_pic ? (
                <img
                  src={photoPreview || user!.profile_pic!}
                  alt={user?.name}
                  className="avatar"
                />
              ) : (
                <div className="avatar-placeholder">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <button
                className="avatar-edit"
                title="Change photo"
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaCamera />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: "none" }}
                onChange={handlePhotoChange}
              />
            </div>
          </div>
          <div className="user-details">
            <h3>{user?.name}</h3>
            <p className="user-email">
              <FaEnvelope />
              {user?.email}
            </p>
            <button
              type="button"
              className="edit-profile-link"
              onClick={() => setIsEditingProfile((v) => !v)}
            >
              <FaPen />
              Edit Name and Photo
            </button>
          </div>
        </div>

        <motion.form
          id="my-account-form"
          className="profile-edit-form"
          onSubmit={handleSubmit(onSubmit)}
          initial={false}
          animate={{
            opacity: isEditingProfile ? 1 : 0,
            height: isEditingProfile ? "auto" : 0,
          }}
          transition={{ duration: 0.2 }}
          style={{ overflow: "hidden" }}
        >
          <div className="form-group compact" style={{ marginTop: 16 }}>
            <label htmlFor="name">Display Name</label>
            <input
              type="text"
              id="name"
              className="form-control"
              {...register("name")}
              placeholder="Enter your name"
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isUpdating}
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setIsEditingProfile(false);
                setPhotoPreview(null);
                setPhotoFile(null);
              }}
            >
              Cancel
            </button>
          </div>
        </motion.form>
      </div>

      <div className="section-divider" />

      {/* Account */}
      <div className="tab-section">
        <h3 className="section-title">Account</h3>
        <div className="security-item">
          <FaGoogle className="security-icon" />
          <div className="security-content">
            <h4>Google Authentication</h4>
            <p>Your account is secured with Google OAuth 2.0</p>
          </div>
          <span className="badge badge-success">Connected</span>
        </div>
      </div>

      <div className="section-divider" />

      {/* Manage */}
      <div className="tab-section">
        <h3 className="section-title">Manage</h3>
        <p className="section-description">
          Download a copy of all your personal data including campaigns, leads,
          emails, and landing pages in JSON format.
        </p>
        <button
          className="btn btn-outline-secondary account-action-btn"
          onClick={handleExportData}
          disabled={isExporting}
          type="button"
        >
          {isExporting ? (
            <>
              <span className="spinner-border spinner-border-sm" /> Preparing...
            </>
          ) : (
            <>
              <FaDownload /> Export My Data
            </>
          )}
        </button>
      </div>

      <div className="section-divider" />

      {/* Delete Account */}
      <div className="tab-section">
        <h3 className="section-title danger-title">Delete Account</h3>
        <p className="section-description">
          Permanently delete your account and all associated data. This action
          cannot be undone. All campaigns, leads, emails, landing pages, and
          connected accounts will be removed.
        </p>
        <button
          className="btn btn-danger-gradient account-action-btn"
          onClick={() => setShowDeleteModal(true)}
          type="button"
        >
          <FaTrash />
          Delete Account
        </button>
      </div>

      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            className="delete-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              className="delete-modal glass"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="delete-modal-close"
                onClick={() => setShowDeleteModal(false)}
                type="button"
              >
                <FaTimes />
              </button>
              <div className="delete-modal-icon">
                <FaExclamationTriangle />
              </div>
              <h3>Delete Your Account?</h3>
              <p>
                This will <strong>permanently delete</strong> your account and
                all data including campaigns, emails, leads, landing pages, and
                connected accounts. This action cannot be undone.
              </p>
              <label className="delete-modal-label">
                Type <strong>DELETE MY ACCOUNT</strong> to confirm:
              </label>
              <input
                type="text"
                className="form-control"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                autoComplete="off"
              />
              <div className="delete-modal-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText("");
                  }}
                  disabled={isDeleting}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger-gradient"
                  onClick={handleDeleteAccount}
                  disabled={
                    deleteConfirmText !== "DELETE MY ACCOUNT" || isDeleting
                  }
                  type="button"
                >
                  {isDeleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash className="btn-icon" />
                      Permanently Delete
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyAccountTab;
